import appOriginsData from '../data/appOrigins.json';

const FALLBACK_PALETTE = [
  '#2563EB',
  '#7C3AED',
  '#059669',
  '#D97706',
  '#DB2777',
  '#DC2626',
  '#0891B2',
  '#4F46E5',
];

const LEGACY_COLORS = {
  TAPINOUT: '#2563EB',
  SEATHUB: '#7C3AED',
  CITYCOIN: '#D97706',
  SERVICEHUB: '#059669',
  NOTIFICATION: '#DB2777',
  SUPPORTHUB: '#DC2626',
};

/** @type {Array<{ code: string, label: string, color?: string, active?: boolean, sortOrder?: number }>|null} */
let appOriginRegistry = null;

const colorFromCodeHash = (code) => {
  const key = String(code).toUpperCase();
  if (LEGACY_COLORS[key]) return LEGACY_COLORS[key];
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) {
    hash = key.charCodeAt(i) + ((hash << 5) - hash);
  }
  return FALLBACK_PALETTE[Math.abs(hash) % FALLBACK_PALETTE.length];
};

export const normalizeAppOriginColor = (color, code = '') => {
  if (typeof color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(color.trim())) {
    return color.trim().toUpperCase();
  }
  return colorFromCodeHash(code);
};

export const setAppOriginRegistry = (definitions) => {
  if (!Array.isArray(definitions)) {
    appOriginRegistry = null;
    return;
  }
  appOriginRegistry = definitions
    .filter((d) => d?.code)
    .map((d) => ({
      code: String(d.code).toUpperCase(),
      label: d.label || d.code,
      color: normalizeAppOriginColor(d.color, d.code),
      active: d.active !== false,
      sortOrder: typeof d.sortOrder === 'number' ? d.sortOrder : 0,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label));
};

export const getAppOriginRegistry = () => appOriginRegistry;

const fallbackLabel = (code) => {
  const key = String(code).toUpperCase();
  return appOriginsData.displayNames[key] ?? String(code);
};

export const getAppOriginLabel = (code) => {
  if (code == null || code === '') return '';
  const key = String(code).toUpperCase();
  const fromApi = appOriginRegistry?.find((o) => o.code === key);
  return fromApi?.label ?? fallbackLabel(key);
};

export const getAppOriginColor = (code) => {
  if (code == null || code === '') return '#B2BEC3';
  const key = String(code).toUpperCase();
  const fromApi = appOriginRegistry?.find((o) => o.code === key);
  return fromApi?.color ?? colorFromCodeHash(key);
};

export const getAppOriginFilterOptions = () => {
  if (appOriginRegistry?.length) {
    return appOriginRegistry
      .filter((o) => o.active)
      .map((o) => ({
        value: o.code,
        label: o.label,
        color: o.color,
      }));
  }
  return appOriginsData.fixedOrigins.map((code) => ({
    value: code,
    label: fallbackLabel(code),
    color: colorFromCodeHash(code),
  }));
};

export const getActiveAppOriginCodes = () => {
  if (appOriginRegistry?.length) {
    return appOriginRegistry.filter((o) => o.active).map((o) => o.code);
  }
  return [...appOriginsData.fixedOrigins];
};

/** @deprecated Use getAppOriginLabel */
export const getDisplayName = (origin) => getAppOriginLabel(origin);

/** @deprecated Use getActiveAppOriginCodes */
export const getFixedOrigins = () => getActiveAppOriginCodes();

/** @deprecated Use getAppOriginFilterOptions */
export const getAllOriginsWithDisplayNames = (dynamicOrigins = []) => {
  const registryCodes = getActiveAppOriginCodes();
  const dynamic = Array.isArray(dynamicOrigins)
    ? dynamicOrigins.filter((o) => o && !registryCodes.includes(String(o).toUpperCase()))
    : [];
  return [...registryCodes, ...dynamic].map((code) => ({
    value: code,
    label: getAppOriginLabel(code),
    color: getAppOriginColor(code),
  }));
};

export class AppOriginService {
  static getFixedOrigins() {
    return getActiveAppOriginCodes();
  }

  static getDisplayName(origin) {
    return getAppOriginLabel(origin);
  }

  static getColor(origin) {
    return getAppOriginColor(origin);
  }

  static filterDynamicOrigins(dynamicOrigins) {
    if (!dynamicOrigins || !Array.isArray(dynamicOrigins)) {
      return [];
    }
    const known = new Set(getActiveAppOriginCodes().map((o) => o.toLowerCase()));
    return dynamicOrigins.filter((origin) => !known.has(String(origin).toLowerCase()));
  }

  static combineOrigins(dynamicOrigins = []) {
    const filteredDynamic = this.filterDynamicOrigins(dynamicOrigins);
    return [...getActiveAppOriginCodes(), ...filteredDynamic];
  }

  static getAllOriginsWithDisplayNames(dynamicOrigins = []) {
    return getAllOriginsWithDisplayNames(dynamicOrigins);
  }
}

export default AppOriginService;
